/* All of this code includes the necessary functions to run and track an A/B experiment
  using Optimizely. Please fully read through the code to know where changes
  need to be made specifically for a new test.

  As of 2025-01-08, this code is functionally the same as the DefaultUpgradeModal.
*/

/* eslint import/no-unresolved: 0 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDecision, withOptimizely } from '@optimizely/react-sdk';
import { sendTrackEvent, sendTrackingLogEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Button } from '@openedx/paragon';

import { UpgradeButton } from '@src/generic/upgrade-button';
import getBulletVariation from '../optimizely/utils';
import { OPTIMIZELY_AUDIT_TRIAL_EXPERIMENT } from '../data';
import BaseModal from './BaseModal';

const ExperimentalUpgradeModal = ({
  isOpen,
  close,
  verifiedMode,
  org,
  courseId,
  offer,
  marketingUrl,
  accessExpiration,
  datesBannerInfo,
  optimizely,
  learningAssistantEnabled,
}) => {
  /*
    Update the following line with the new experiment constants.
    KEY and ANY_CLICK_ACTION are required and used in the later in the code.
    To update the variables, make changes in ../optimizely/utils
  */
  const { KEY: experimentKey, ANY_CLICK_ACTION } = OPTIMIZELY_AUDIT_TRIAL_EXPERIMENT;

  const [decision] = useDecision(experimentKey);
  const { variationKey, enabled } = decision;

  // Experiment specific properties or data should not be sent to the tracking logs, so break this
  // object out to ensure it is only sent to Segment and not the tracking logs.
  const experimentProperties = {
    experiment_key: experimentKey,
    variation_key: variationKey,
  };

  const upsellPageName = 'course_sequence_modal';
  const eventProperties = {
    org_key: org,
    courserun_key: courseId,
  };
  const promotionEventProperties = {
    creative: 'marketmodalupsell',
    name: 'In-Course Verification Prompt',
    position: 'marketing-modal-message',
    promotion_id: 'courseware_verified_certificate_upsell',
    ...eventProperties,
    ...experimentProperties,
  };

  useEffect(() => {
    sendTrackingLogEvent('edx.bi.course.upgrade.marketmodalupsell.displayed', eventProperties);
    sendTrackEvent('Promotion Viewed', promotionEventProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logClick = () => {
    sendTrackingLogEvent('edx.bi.course.upgrade.marketingmodal.clicked', eventProperties);
    sendTrackingLogEvent('edx.course.enrollment.upgrade.clicked', {
      ...eventProperties,
      location: 'marketing-modal-message',
    });
    sendTrackEvent('Promotion Clicked', promotionEventProperties);
    sendTrackEvent('edx.bi.ecommerce.upsell_links_clicked', {
      ...eventProperties,
      ...experimentProperties,
      linkCategory: 'green_upgrade',
      linkName: `${upsellPageName}_green`,
      linkType: 'button',
      pageName: upsellPageName,
    });

    /*
      Confirm that the ANY_CLICK_ACTION matches the event key in Optimizely.
      Add any additional tracking for Optimizely here.
    */
    optimizely.track(ANY_CLICK_ACTION);
  };
  const logClickPastExpiration = () => {
    sendTrackEvent('edx.bi.ecommerce.upgrade_modal.past_expiration.button_clicked', {
      ...eventProperties,
      ...experimentProperties,
      linkCategory: 'upgrade_modal',
      linkName: `${upsellPageName}_course_details`,
      linkType: 'button',
      pageName: upsellPageName,
    });
  };

  const intl = useIntl();
  const dateNow = Date.now();
  const upgradeDeadlineDate = accessExpiration ? new Date(accessExpiration.upgradeDeadline) : null;
  const pastDeadline = accessExpiration ? new Date(dateNow) > upgradeDeadlineDate : false;
  const isFBE = !!accessExpiration && !!datesBannerInfo?.contentTypeGatingEnabled;

  /*
    Update the enabled variation according to your experiment
  */
  const upsellMessage = getBulletVariation({
    enabled,
    // temporary assignement while not running experiment to force component
    variationKey: 'table',
    isFBE,
    learningAssistantEnabled,
  });

  let callToActionButton = (
    <UpgradeButton
      offer={offer}
      onClick={logClick}
      verifiedMode={verifiedMode}
      block
    />
  );

  if (pastDeadline) {
    callToActionButton = (
      <Button
        className="w-100"
        onClick={logClickPastExpiration}
        href={marketingUrl}
      >
        {intl.formatMessage(
          {
            id: 'learning.upgrade-modal.upgrade-button.label',
            defaultMessage: 'View course details',
            description: 'Label for upgrade button',
          },
        )}
      </Button>
    );
  }

  return (
    <BaseModal
      {...{
        isOpen,
        close,
        courseId,
        offer,
        callToActionButton,
      }}
    >
      {upsellMessage}
    </BaseModal>
  );
};

ExperimentalUpgradeModal.defaultProps = {
  verifiedMode: {},
  offer: null,
  accessExpiration: null,
  datesBannerInfo: null,
};
ExperimentalUpgradeModal.propTypes = {
  verifiedMode: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currencySymbol: PropTypes.string.isRequired,
    upgradeUrl: PropTypes.string.isRequired,
  }),
  offer: PropTypes.shape({
    code: PropTypes.string,
  }),
  accessExpiration: PropTypes.shape({
    upgradeDeadline: PropTypes.string,
  }),
  datesBannerInfo: PropTypes.shape({
    contentTypeGatingEnabled: PropTypes.bool,
  }),
  marketingUrl: PropTypes.string.isRequired,
  org: PropTypes.string.isRequired,
  courseId: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  /* eslint-disable-next-line react/forbid-prop-types */
  optimizely: PropTypes.object.isRequired,
  learningAssistantEnabled: PropTypes.bool.isRequired,
};

export default withOptimizely(ExperimentalUpgradeModal);
